import { useState, useEffect, useCallback, useMemo } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FilterBar } from './components/FilterBar';
import { ClientDrawer } from './components/ClientDrawer';
import { AlertDrawer } from './components/AlertDrawer';
import { ToastContainer, useToast } from './components/Toast';
import { Spinner, TrustLegend } from './components/StatusChip';
import { api } from './api/client';
import { timeAgo } from './api/format';
import { useModule } from './state/useModule';
import { useFilters, useTabState } from './state/useURLState';
import type { Filters } from './state/useURLState';
import type { OverviewResponse, SystemHealthResponse, AlertsResponse, PipelineResponse, PacingResponse, RunRateResponse, InboxResponse, DomainHealthResponse, DiagnosticsResponse, OverviewClient, Alert } from './api/types';

import { AlertsModule } from './modules/Alerts';
import { ActionsModule } from './modules/Actions';
import { PacingModule } from './modules/Pacing';
import { InboxModule } from './modules/Inbox';
import { DomainHealthModule } from './modules/DomainHealth';
import { PipelineModule } from './modules/Pipeline';
import { OverviewModule } from './modules/Overview';
import { RunRateModule } from './modules/RunRate';
import { SystemModule } from './modules/System';
import { DiagnosticsModule } from './modules/Diagnostics';

interface TabDef { id: string; label: string; badge?: number; shortcut?: string }

const TAB_SHORTCUTS: Record<string, string> = {
  a: 'alerts', x: 'actions', p: 'pacing', i: 'inbox',
  d: 'domain-health', l: 'pipeline', o: 'overview',
  r: 'run-rate', s: 'system', g: 'diagnostics',
};

export default function App() {
  const [activeTab, setTab] = useTabState('alerts');
  const { filters, setFilter, clearFilters } = useFilters();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [drawerClient, setDrawerClient] = useState<OverviewClient | null>(null);
  const [drawerAlert, setDrawerAlert] = useState<Alert | null>(null);
  const [pinnedClients, setPinnedClients] = useState<Set<string>>(new Set());
  const [showLegend, setShowLegend] = useState(false);
  const toast = useToast();

  // ── Modules ───────────────────────────────────────────────────────
  const overview = useModule<OverviewResponse>({
    fetcher: s => api.overview(s), extractFreshness: d => d.data_as_of,
    extractTruth: d => ({ hasShadowTruth: (d.missive_completeness?.unresolved_total ?? 0) > 0, isIncomplete: !d.missive_completeness?.is_fresh }),
  });
  const systemHealth = useModule<SystemHealthResponse>({ fetcher: s => api.systemHealth(s) });
  const alerts = useModule<AlertsResponse>({ fetcher: s => api.alerts(s) });
  const pipeline = useModule<PipelineResponse>({ fetcher: s => api.pipeline(s), extractFreshness: d => d.data_as_of });
  const pacing = useModule<PacingResponse>({
    fetcher: s => api.pacing(s),
    extractTruth: d => ({ isProxy: d.pacing.some(r => r.is_proxy === 1), hasInsufficientHistory: d.pacing.some(r => !r.measurable) }),
  });
  const runRate = useModule<RunRateResponse>({
    fetcher: s => api.runRate(s),
    extractTruth: d => ({ hasInsufficientHistory: d.run_rates.some(r => r.confidence === 'low'), confidence: d.run_rates.length > 0 ? d.run_rates[0]!.confidence : null }),
  });
  const inbox = useModule<InboxResponse>({
    fetcher: s => api.inbox(s), extractFreshness: d => d.data_as_of,
    extractTruth: d => ({ hasShadowTruth: (d.shadow_count?.unresolved_total ?? 0) > 0, isIncomplete: d.shadow_count?.conversations_truncated ?? false }),
  });
  const domainHealth = useModule<DomainHealthResponse>({ fetcher: s => api.domainHealth(s) });
  const diagnostics = useModule<DiagnosticsResponse>({ fetcher: s => api.diagnostics(s) });

  // ── Reload ────────────────────────────────────────────────────────
  const reloadAll = useCallback(() => {
    overview.refetch(); systemHealth.refetch(); alerts.refetch();
    pipeline.refetch(); pacing.refetch(); runRate.refetch();
    inbox.refetch(); domainHealth.refetch(); diagnostics.refetch();
  }, [overview, systemHealth, alerts, pipeline, pacing, runRate, inbox, domainHealth, diagnostics]);

  // ── Sync: polls collector completion against trigger timestamp ─────
  const triggerSync = useCallback(async () => {
    setSyncing(true); setSyncError(null);
    const triggerTime = Date.now();
    try {
      await api.triggerRefresh();
      toast.show('Sync triggered — waiting for collectors...', 'info');

      // Poll system-health: every collector must have last_completed AFTER triggerTime
      let complete = false;
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 2500));
        try {
          const sh = await api.systemHealth();
          const allDone = sh.collectors.every(c => {
            if (!c.last_completed) return false;
            const ts = new Date(c.last_completed.replace(' ', 'T') + 'Z').getTime();
            return ts > triggerTime - 5000; // 5s tolerance for clock skew
          });
          if (allDone) { complete = true; break; }
        } catch { /* keep polling */ }
      }

      if (complete) {
        toast.success('All collectors finished');
      } else {
        setSyncError('Some collectors may not have finished. Data will update on next cron.');
        toast.error('Sync incomplete — partial data may be stale');
      }
      reloadAll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed';
      setSyncError(msg); toast.error(msg);
    } finally { setSyncing(false); }
  }, [reloadAll, toast]);

  // ── Apply saved view ───────────────────────────────────────────────
  const applyView = useCallback((view: { filters: Partial<Filters>; tab?: string }) => {
    clearFilters();
    for (const [k, v] of Object.entries(view.filters)) {
      setFilter(k as keyof Filters, v as any);
    }
    if (view.tab) setTab(view.tab);
  }, [clearFilters, setFilter, setTab]);
  useEffect(() => {
    let gPressed = false;
    let gTimeout: ReturnType<typeof setTimeout>;

    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;

      if (e.key === '/') { e.preventDefault(); document.querySelector<HTMLInputElement>('input[type="text"]')?.focus(); return; }
      if (e.key === 'Escape') { setDrawerClient(null); setDrawerAlert(null); return; }

      // g+key tab navigation
      if (e.key === 'g' && !gPressed) {
        gPressed = true;
        clearTimeout(gTimeout);
        gTimeout = setTimeout(() => { gPressed = false; }, 500);
        return;
      }
      if (gPressed && TAB_SHORTCUTS[e.key]) {
        e.preventDefault();
        setTab(TAB_SHORTCUTS[e.key]!);
        gPressed = false;
        return;
      }
      gPressed = false;
    };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); clearTimeout(gTimeout); };
  }, [setTab]);

  // ── Pinned clients ────────────────────────────────────────────────
  const togglePin = useCallback((clientId: string) => {
    setPinnedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  }, []);

  // ── Derived ───────────────────────────────────────────────────────
  const clients = useMemo(() => (overview.data?.clients || []).map(c => ({ id: c.id, name: c.name })), [overview.data]);
  const workspaces = useMemo(() => (domainHealth.data?.workspaces || []).map(w => ({ id: w.id, name: w.client_name })), [domainHealth.data]);
  const openAlerts = (alerts.data?.alerts || []).filter(a => a.state === 'open');
  const redAlerts = openAlerts.filter(a => a.severity === 'red').length;
  const failingCollectors = (systemHealth.data?.collectors || []).filter(c => c.consecutive_failures).length;
  const offTrackClients = useMemo(() => new Set((pacing.data?.pacing || []).filter(r => r.pace_status === 'off_track').map(r => r.client_id)).size, [pacing.data]);
  const unresolved = inbox.data?.shadow_count?.unresolved_total ?? 0;
  const syncOk = overview.dataFreshness?.last_run_status?.startsWith('completed');
  const lastSync = overview.dataFreshness?.last_run_status;

  // Crash counts for diagnostics
  const totalCrashes = overview.crashCount + systemHealth.crashCount + alerts.crashCount + pipeline.crashCount + pacing.crashCount + runRate.crashCount + inbox.crashCount + domainHealth.crashCount;

  // ── Drawer data ───────────────────────────────────────────────────
  const drawerAlerts = useMemo(() => drawerClient ? (alerts.data?.alerts || []).filter(a => a.client_id === drawerClient.id) : [], [drawerClient, alerts.data]);
  const drawerPacing = useMemo(() => drawerClient ? (pacing.data?.pacing || []).filter(p => p.client_id === drawerClient.id) : [], [drawerClient, pacing.data]);
  const drawerRunRate = useMemo(() => drawerClient ? (runRate.data?.run_rates || []).find(r => r.client_id === drawerClient.id) ?? null : null, [drawerClient, runRate.data]);

  const openClientDrawer = useCallback((clientId: string) => {
    const client = overview.data?.clients.find(c => c.id === clientId);
    if (client) setDrawerClient(client);
  }, [overview.data]);

  const handleAlertDrawerAction = useCallback(() => { toast.success('Alert updated'); alerts.refetch(); }, [alerts, toast]);

  const tabs: TabDef[] = [
    { id: 'alerts', label: 'Alerts', badge: openAlerts.length, shortcut: 'g a' },
    { id: 'actions', label: 'Actions', shortcut: 'g x' },
    { id: 'pacing', label: 'Pacing', shortcut: 'g p' },
    { id: 'inbox', label: 'Inbox', shortcut: 'g i' },
    { id: 'domain-health', label: 'Domain', shortcut: 'g d' },
    { id: 'pipeline', label: 'Pipeline', shortcut: 'g l' },
    { id: 'overview', label: 'Overview', shortcut: 'g o' },
    { id: 'run-rate', label: 'Run-Rate', shortcut: 'g r' },
    { id: 'system', label: 'System', badge: failingCollectors > 0 ? failingCollectors : undefined, shortcut: 'g s' },
    { id: 'diagnostics', label: 'Diag', badge: totalCrashes > 0 ? totalCrashes : undefined, shortcut: 'g g' },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* ══ HEADER ══════════════════════════════════════════════════ */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: 5, background: 'linear-gradient(135deg, var(--blue), var(--purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff' }}>A</div>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-primary)' }}>ASTRIS</span>
            <span style={{ fontSize: 11, color: 'var(--text-ghost)', fontWeight: 500, borderLeft: '1px solid var(--text-dead)', paddingLeft: 9, marginLeft: 1 }}>Command Center</span>
          </div>
          <nav style={{ display: 'flex', gap: 1, marginLeft: 12, flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} title={t.shortcut}
                style={{ padding: '5px 11px', borderRadius: 5, border: 'none', background: activeTab === t.id ? 'var(--bg-active)' : 'transparent', color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-ghost)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all var(--ease-fast)', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                {t.label}
                {(t.badge ?? 0) > 0 && <span style={{ background: t.id === 'system' ? 'var(--yellow-bg)' : t.id === 'diagnostics' ? 'var(--orange-bg)' : 'var(--red-bg)', color: t.id === 'system' ? 'var(--yellow)' : t.id === 'diagnostics' ? 'var(--orange)' : 'var(--red)', border: `1px solid ${t.id === 'system' ? 'var(--yellow-border)' : t.id === 'diagnostics' ? 'var(--orange-border)' : 'var(--red-border)'}`, fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 10 }}>{t.badge}</span>}
              </button>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: syncOk ? 'var(--text-ghost)' : 'var(--red)' }}>{lastSync || (overview.status === 'loading' ? 'loading...' : 'unknown')}</div>
          </div>
          <button onClick={reloadAll} disabled={syncing} title="Refetch all views" style={{ padding: '5px 12px', borderRadius: 5, border: '1px solid var(--border-subtle)', background: 'var(--bg-hover)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.5 : 1 }}>↻ Reload</button>
          <button onClick={triggerSync} disabled={syncing} style={{ padding: '5px 12px', borderRadius: 5, border: '1px solid var(--blue-border)', background: syncing ? 'var(--bg-active)' : 'var(--blue-bg)', color: syncing ? 'var(--text-ghost)' : 'var(--blue)', fontSize: 11, fontWeight: 600, cursor: syncing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            {syncing && <Spinner size={11} />}{syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </header>

      {syncError && <div style={{ padding: '8px 24px', background: 'var(--red-bg)', borderBottom: '1px solid var(--red-border)', fontSize: 12, color: 'var(--red)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>⚠ {syncError}</span><button onClick={() => setSyncError(null)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>×</button></div>}

      {/* ══ STATUS RAIL ══════════════════════════════════════════════ */}
      <div style={{ display: 'flex', gap: 16, padding: '8px 24px', borderBottom: '1px solid var(--border-faint)', background: 'var(--bg-root)', fontSize: 11, alignItems: 'center' }}>
        {redAlerts > 0 && <span style={{ color: 'var(--red)', fontWeight: 700 }}>● {redAlerts} red</span>}
        {openAlerts.length > 0 && redAlerts === 0 && <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>{openAlerts.length} open</span>}
        {openAlerts.length === 0 && <span style={{ color: 'var(--green)' }}>✓ No alerts</span>}
        <span style={{ color: 'var(--text-dead)' }}>|</span>
        {offTrackClients > 0 ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{offTrackClients} off track</span> : <span style={{ color: 'var(--text-ghost)' }}>Pacing {pacing.status === 'loading' ? '...' : 'ok'}</span>}
        <span style={{ color: 'var(--text-dead)' }}>|</span>
        {unresolved > 0 ? <span style={{ color: 'var(--yellow)' }}>{unresolved} unresolved</span> : <span style={{ color: 'var(--text-ghost)' }}>Inbox {inbox.status === 'loading' ? '...' : 'clear'}</span>}
        {failingCollectors > 0 && <><span style={{ color: 'var(--text-dead)' }}>|</span><span style={{ color: 'var(--red)' }}>{failingCollectors} failing</span></>}
        {totalCrashes > 0 && <><span style={{ color: 'var(--text-dead)' }}>|</span><span style={{ color: 'var(--orange)' }}>{totalCrashes} fetch errors</span></>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setShowLegend(!showLegend)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}>{showLegend ? 'hide legend' : 'legend'}</button>
          <span style={{ color: 'var(--text-dim)' }}>{syncOk ? '✓' : '⚠'} {overview.dataFreshness?.last_success ? timeAgo(overview.dataFreshness.last_success) : 'no data'}</span>
        </span>
      </div>

      {/* Trust legend (toggleable) */}
      {showLegend && <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border-faint)', background: 'var(--bg-root)' }}><TrustLegend /></div>}

      {/* ══ FILTERS ═════════════════════════════════════════════════ */}
      <div style={{ padding: '16px 24px 0', maxWidth: 1400, margin: '0 auto' }}>
        <FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} clients={clients} workspaces={workspaces} onApplyView={applyView} />
        {/* Pinned clients quick-access */}
        {pinnedClients.size > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--text-ghost)', alignSelf: 'center' }}>Pinned:</span>
            {[...pinnedClients].map(id => {
              const c = clients.find(x => x.id === id);
              return (
                <button key={id} onClick={() => setFilter('client', filters.client === id ? null : id)}
                  style={{ padding: '3px 8px', fontSize: 11, borderRadius: 'var(--radius-sm)', border: `1px solid ${filters.client === id ? 'var(--blue-border)' : 'var(--border-subtle)'}`, background: filters.client === id ? 'var(--blue-bg)' : 'var(--bg-surface)', color: filters.client === id ? 'var(--blue)' : 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  📌 {c?.name || id}
                  <span onClick={(e) => { e.stopPropagation(); togglePin(id); }} style={{ cursor: 'pointer', fontSize: 10, color: 'var(--text-ghost)' }}>×</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ══ MAIN ════════════════════════════════════════════════════ */}
      <main style={{ padding: '0 24px 24px', maxWidth: 1400, margin: '0 auto' }}>
        <ErrorBoundary module="app-content">
          {activeTab === 'alerts' && <AlertsModule moduleState={alerts} filters={filters} onAlertClick={a => setDrawerAlert(a)} />}
          {activeTab === 'actions' && <ActionsModule moduleState={alerts} />}
          {activeTab === 'pacing' && <PacingModule moduleState={pacing} filters={filters} />}
          {activeTab === 'inbox' && <InboxModule moduleState={inbox} filters={filters} />}
          {activeTab === 'domain-health' && <DomainHealthModule moduleState={domainHealth} filters={filters} systemHealth={systemHealth} />}
          {activeTab === 'pipeline' && <PipelineModule moduleState={pipeline} filters={filters} />}
          {activeTab === 'overview' && <OverviewModule moduleState={overview} systemHealth={systemHealth} filters={filters} onClientClick={openClientDrawer} onPinClient={togglePin} pinnedClients={pinnedClients} />}
          {activeTab === 'run-rate' && <RunRateModule moduleState={runRate} filters={filters} />}
          {activeTab === 'system' && <SystemModule moduleState={systemHealth} shadowUnresolved={unresolved} missiveIncomplete={inbox.data?.shadow_count?.conversations_truncated} />}
          {activeTab === 'diagnostics' && <DiagnosticsModule moduleState={diagnostics} crashCount={totalCrashes} version={systemHealth.data?.version} collectors={systemHealth.data?.collectors} />}
        </ErrorBoundary>
      </main>

      <ClientDrawer client={drawerClient} alerts={drawerAlerts} pacing={drawerPacing} runRate={drawerRunRate} shadowCount={inbox.data?.shadow_count ?? null} onClose={() => setDrawerClient(null)} />
      <AlertDrawer alert={drawerAlert} onClose={() => setDrawerAlert(null)} onAction={handleAlertDrawerAction} />
      <ToastContainer toasts={toast.toasts} />
    </div>
  );
}
